import React, { ReactElement } from 'react';
import { useSelector } from '@ecommerce/nest-ssr/common';

import type { ReviewStatsPeriodicInterface } from 'interfaces/review-stats.interface';
import Icon from 'atomic/icon/icon';
import Tooltip from 'atomic/tooltip';

import './rating-table.scss';

interface CompanyViewCardRatingTableProperties {
    activeTab: number;
}

const CompanyViewCardRatingTable: React.FC<CompanyViewCardRatingTableProperties> = ({ activeTab }): ReactElement => {
    function prepareData(): {
        lastMonthStats: ReviewStatsPeriodicInterface | undefined;
        lastYearStats: ReviewStatsPeriodicInterface | undefined;
        everStats: ReviewStatsPeriodicInterface | undefined;
        } {
        const stats = useSelector((state: {
            reviewStatsPeriodic: ReviewStatsPeriodicInterface[];
            reviewStatsPeriodicCredible: ReviewStatsPeriodicInterface[];
        }) => ({
            reviewStatsPeriodic: state.reviewStatsPeriodic,
            reviewStatsPeriodicCredible: state.reviewStatsPeriodicCredible
        }));

        let data: ReviewStatsPeriodicInterface[] = stats.reviewStatsPeriodic;

        if (activeTab === 1) {
            data = stats.reviewStatsPeriodicCredible;
        }

        return {
            lastMonthStats: data.find(item => item.period === 'thirtyDays'),
            lastYearStats: data.find(item => item.period === 'oneYear'),
            everStats: data.find(item => item.period === 'ever')
        };
    }

    const {
        lastMonthStats,
        lastYearStats,
        everStats
    } = prepareData();

    return (
        <table className="company-view-card__rating-table">
            <thead>
                <tr>
                    <th className="company-view-card__rating-table-title">
                        &nbsp;
                    </th>
                    <th className="company-view-card__rating-table-title">
                        30 dni
                    </th>
                    <th className="company-view-card__rating-table-title">
                        12 miesięcy
                    </th>
                    <th className="company-view-card__rating-table-title">
                        Wszystkie
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <th colSpan={4} className="company-view-card__rating-table-header">
                        Opinie
                    </th>
                </tr>
                <tr>
                    <td className="company-view-card__rating-table-cell company-view-card__rating-table-cell--first">
                        <Tooltip
                            message="Opinie pozytywne to opinie, które wyrażają zadowolenie z produktu lub przedsiębiorcy.
                                Jako pozytywne uznawane są opinie, dla których średnia z ocen cząstkowych wynosi 3.25 punktu lub więcej."
                        >
                            <Icon icon="thumb-up-circle" className="company-view-card__rating-table-icon" />
                        </Tooltip>
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastMonthStats?.positiveCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastYearStats?.positiveCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {everStats?.positiveCount || 0}
                    </td>
                </tr>
                <tr>
                    <td className="company-view-card__rating-table-cell">
                        <Tooltip
                            message="Ocena neutralna to ocena, która nie wyraża ani zadowolenia, ani niezadowolenia
                                z produktu lub przedsiębiorcy. Jako neutralne uznawane są opinie, dla których średnia z ocen cząstkowych
                                mieści się w zakresie większym niż 2.25 i mniejszym niż 3.25 punktu."
                        >
                            <Icon icon="thumb-right-circle" className="company-view-card__rating-table-icon" />
                        </Tooltip>
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastMonthStats?.neutralCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastYearStats?.neutralCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {everStats?.neutralCount || 0}
                    </td>
                </tr>
                <tr>
                    <td className="company-view-card__rating-table-cell">
                        <Tooltip
                            message="Ocena negatywna to ocena, która wyraża niezadowolenie z produktu lub przedsiębiorcy.
                                Jako negatywne uznawane są opinie, dla których średnia z ocen cząstkowych wynosi 2.25 punktu lub mniej."
                        >
                            <Icon icon="thumb-down-circle" className="company-view-card__rating-table-icon" />
                        </Tooltip>
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastMonthStats?.negativeCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastYearStats?.negativeCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {everStats?.negativeCount || 0}
                    </td>
                </tr>
                <tr>
                    <th colSpan={4} className="company-view-card__rating-table-header">
                        Opinie wycofane/usunięte
                    </th>
                </tr>
                <tr>
                    <td className="company-view-card__rating-table-cell company-view-card__rating-table-cell--first">
                        <Tooltip
                            message="Opiniujący ma prawo do wycofania swojej opinii w OpiConnect, jeśli zmieni
                                zdanie na temat opiniowanego przedsiębiorcy lub produktu. Wycofanie opinii może nastąpić
                                z różnych powodów, takich jak chęć ponownego ocenienia, uwzględnienie reklamacji, dojście
                                do porozumienia lub nieaktualność opinii."
                        >
                            <Icon icon="opiconnect-withdrawn" className="company-view-card__rating-table-icon" />
                        </Tooltip>
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastMonthStats?.withdrawnByOpiconnectUserCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastYearStats?.withdrawnByOpiconnectUserCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {everStats?.withdrawnByOpiconnectUserCount || 0}
                    </td>
                </tr>
                <tr>
                    <td className="company-view-card__rating-table-cell">
                        <Tooltip
                            message="Opinie wycofane w wyniku wykonywania przez Opineo obowiązków wynikających
                                z UŚUDE lub w wyniku złamania regulaminu Opineo."
                        >
                            <Icon icon="opiconnect-illegal" className="company-view-card__rating-table-icon" />
                        </Tooltip>
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastMonthStats?.withdrawnByCustomerServiceCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {lastYearStats?.withdrawnByCustomerServiceCount || 0}
                    </td>
                    <td className="company-view-card__rating-table-cell">
                        {everStats?.withdrawnByCustomerServiceCount || 0}
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

export default CompanyViewCardRatingTable;
